import { FC, useMemo } from 'react';

import spot from 'data/spot.json';

import { useDialog } from 'hooks/useDialog';
import { useGetProgressData } from 'hooks/useGetProgressData';
import { usePageTitle } from 'hooks/usePageTitle';

import { CompletedArea } from 'components/features/CompletedArea';
import { RecentlyExploredArea } from 'components/features/RecentlyExploredArea';
import { Button } from 'components/parts/Button';
import { HamadoriHeadline } from 'components/parts/HamadoriHeadline';
import { Paragraph } from 'components/parts/Paragraph';

import { AreaStatus, ProgressResponse } from 'store/progress';

import styles from './Area.module.scss';

import { AreaCard } from './components/AreaCard';

import type { SpotData } from 'types/spot';

import MissionTitleImage from 'images/area/title_mission.svg';
import { AreaCardItem } from 'components/pages/Area/components/AreaCard/AreaCard';
import { MapDialogView } from 'components/pages/Area/components/MapDialogView/MapDialogView';

type AreaCardItemWithKey = AreaCardItem & {
  key: string;
};

function createCardItems(
  spotList: SpotData[],
  progressData: ProgressResponse | undefined
): {
  notHamadoriList: AreaCardItem[];
  hamadoriList: AreaCardItem[];
  missionList: AreaCardItemWithKey[];
} {
  // 浜通り地方以外
  const notHamadoriList = spotList
    .filter((spot: SpotData) => spot.group !== '浜通り地方')
    .map(spot => {
      const { id, name, difficultyLevel } = spot;
      const areaStatus = progressData?.areaStatusList.find((item: AreaStatus) => {
        const find = String(item.areaNo) === id;
        return find;
      });
      return {
        id,
        name,
        areaNo: Number(id),
        difficultyLevel,
        clueStatus: {
          clueCount: spot?.clueCount,
          foundClue_1: areaStatus?.foundClue_1 ?? false,
          foundClue_2: areaStatus?.foundClue_2 ?? false,
          foundClue_3: areaStatus?.foundClue_3 ?? false,
        },
        cleared: areaStatus?.completed ?? false,
        isHamadori: false,
      } satisfies AreaCardItem;
    });

  // 浜通り地方のみ
  const hamadoriList = spotList
    .filter((spot: SpotData) => spot.group === '浜通り地方')
    .map(spot => {
      const { id, name, difficultyLevel } = spot;
      const areaStatus = progressData?.areaStatusList.find((item: AreaStatus) => {
        const find = String(item.areaNo) === id;
        return find;
      });
      return {
        id,
        name,
        areaNo: Number(id),
        difficultyLevel,
        clueStatus: {
          clueCount: spot?.clueCount,
          foundClue_1: areaStatus?.foundClue_1 ?? false,
          foundClue_2: areaStatus?.foundClue_2 ?? false,
          foundClue_3: areaStatus?.foundClue_3 ?? false,
        },
        cleared: areaStatus?.completed ?? false,
        isHamadori: true,
      } satisfies AreaCardItem;
    });

  const missionList = spotList
    .filter(spot => spot.mission)
    .flatMap(spot => {
      return (
        spot.mission?.map(mission => {
          const isClear = progressData?.missionStatusList.find(
            status => status.missionNo === mission.missionNo
          )?.completed;
          return {
            key: `${spot.id}_${mission.missionNo}`,
            id: spot.id,
            name: spot.name,
            cleared: isClear ?? false,
            missionNo: mission.missionNo,
            isHamadori: spot.group === '浜通り地方',
          } satisfies AreaCardItemWithKey;
        }) ?? []
      );
    });

  return {
    notHamadoriList,
    hamadoriList,
    missionList,
  };
}

export const Area: FC = () => {
  usePageTitle(`探索エリア`);
  const { isOpen, handleOpen, handleClose } = useDialog();

  const { data: progressData } = useGetProgressData();
  const spotList: SpotData[] = spot as unknown as SpotData[];
  const recentlyExploredAreaNo = progressData?.recentlyExploredAreaNo;

  const isAllClear = progressData?.completedAreaList.length === 19;
  const completedAreas = progressData?.completedAreaList.length || 0;

  const { notHamadoriList, hamadoriList, missionList } = useMemo(
    () => createCardItems(spotList, progressData),
    [spotList, progressData]
  );

  const recentlyExploredAreaCardItem = useMemo(() => {
    return (
      notHamadoriList.find(item => item.areaNo === recentlyExploredAreaNo) ??
      hamadoriList.find(item => item.areaNo === recentlyExploredAreaNo)
    );
  }, [notHamadoriList, hamadoriList, recentlyExploredAreaNo]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <div className={styles.leadArea}>
            <div className={styles.lead}>
              <Paragraph>探索したいエリアを選ぼう。</Paragraph>
            </div>
            <div className={styles.leadMap}>
              <Button
                text="エリアMAP"
                styleType="secondaryMini"
                handleClick={() => handleOpen()}
                addClass={[styles.spotButton]}
              />
            </div>
          </div>
          <div className={styles.completedArea}>
            <CompletedArea completedAreas={completedAreas} />
          </div>
          {recentlyExploredAreaCardItem && !isAllClear && (
            <div className={styles.recentlyExploredArea}>
              <RecentlyExploredArea cardItem={recentlyExploredAreaCardItem} />
            </div>
          )}
        </header>

        <div className={styles.body}>
          <ul className={styles.areaList}>
            {notHamadoriList.map(item => (
              <li key={item.id} className={styles.areaItem}>
                <AreaCard cardItem={item} />
              </li>
            ))}
          </ul>
          <HamadoriHeadline className={styles.hamadoriHeadline}>
            <div className={styles.hamadoriHeadBackground} />
            <h2 className={styles.hamadoriTitle}>浜通りエリア</h2>
          </HamadoriHeadline>
          <ul className={styles.areaList}>
            {hamadoriList.map(item => (
              <li key={item.id} className={styles.areaItem}>
                <AreaCard cardItem={item} />
              </li>
            ))}
          </ul>
          <div className={styles.missionTitle}>
            <img src={MissionTitleImage} alt="追加ミッション" />
          </div>
          <ul className={styles.areaList}>
            {missionList.map(item => (
              <li key={item.key} className={styles.areaItem}>
                <AreaCard cardItem={item} />
              </li>
            ))}
          </ul>
        </div>
      </div>
      {isOpen && <MapDialogView isOpen={isOpen} handleClose={handleClose} />}
    </div>
  );
};
